import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import bannerEn from '../assets/anima-harvest.jpg';
import bannerFr from '../assets/moisson-d-anima.jpg';
import { CalendarIcon, ChevronIcon, CollectionIcon, TrophyIcon } from '../components/Icons';
import { RarityStrip } from '../components/RarityStrip';
import { ErrorState, Spinner } from '../components/States';
import { BoosterPack } from '../features/gacha/BoosterPack';
import { QuestRow } from '../features/quests/QuestRow';
import { boosterName } from '../i18n/catalog';
import { formatNumber, timeUntil } from '../services/format';
import { useAchievements, useGacha, useLoginCalendar, useMe, usePublicGameStats, useQuests, useSession, useSocialSummary } from '../services/queries';

export function HomePage() {
  const { t, i18n } = useTranslation('pages');
  const me = useMe();
  const user = useSession().data?.user;
  const gacha = useGacha();
  const quests = useQuests();
  const login = useLoginCalendar();
  const publicStats = usePublicGameStats();
  const summary = useSocialSummary(true);
  const achievements = useAchievements();
  const stats = me.data?.stats;
  const dailyClaimable = quests.data?.daily.quests.filter((quest) => quest.completed && !quest.claimed).length ?? 0;
  const weeklyClaimable = quests.data?.weekly.quests.filter((quest) => quest.completed && !quest.claimed).length ?? 0;
  const monthlyClaimable = quests.data?.monthly.quests.filter((quest) => quest.completed && !quest.claimed).length ?? 0;
  const loginReady = summary.data?.claimableLogin ?? login.data?.claimable ?? false;
  const achievementsReady = achievements.data?.achievements.reduce((sum, achievement) => sum + achievement.claimable, 0) ?? 0;
  const stock = gacha.data?.freeBooster;
  const english = i18n.language.startsWith('en');

  return (
    <div className="page home" data-screen="home">
      <header className="page-header home__header">
        <h1>{user ? t('home.welcome', { name: user.username }) : t('home.title')}</h1>
        <p>{t('home.intro')}</p>
      </header>

      <section className="home-play" aria-label={t('home.play.title')}>
        <Link to="/boosters" className="home-play__main">
          <div className="home-play__copy">
            <h2>{t('home.play.title')}</h2>
            <p>
              {gacha.isError
                ? t('home.play.unavailable')
                : stock
                  ? stock.charges > 0
                    ? t('home.play.available', { count: stock.charges })
                    : stock.nextChargeAt
                      ? t('home.play.next', { time: timeUntil(stock.nextChargeAt) })
                      : t('home.play.empty')
                  : t('common:states.loading')}
            </p>
            <span className="home-play__cta">{t('home.play.choose')}<ChevronIcon size={18} /></span>
          </div>
          <div className="home-play__pack" aria-hidden="true">
            <BoosterPack type="free" name={boosterName('free', 'Booster gratuit')} size="sm" />
          </div>
        </Link>
        <div className="home-shortcuts">
          <Link to="/calendrier" className={`home-shortcut${loginReady ? ' home-shortcut--ready' : ''}`}>
            <CalendarIcon size={22} />
            <span className="home-shortcut__copy">
              <strong>{t('home.login.title')}</strong>
              <span>{login.isError ? t('home.login.unavailable') : login.data ? loginReady ? t('home.login.ready') : t('home.login.combo', { count: login.data.streak }) : t('common:states.loading')}</span>
            </span>
            <ChevronIcon size={18} />
          </Link>
          <Link to="/quetes" className={`home-shortcut${achievementsReady > 0 ? ' home-shortcut--ready' : ''}`}>
            <TrophyIcon size={22} />
            <span className="home-shortcut__copy">
              <strong>{t('home.quests.achievements')}</strong>
              <span>{achievements.isError ? t('home.quests.unavailable') : achievements.data ? achievementsReady > 0 ? t('home.quests.claimable', { count: achievementsReady }) : t('home.quests.progress') : t('common:states.loading')}</span>
            </span>
            <ChevronIcon size={18} />
          </Link>
        </div>
      </section>

      <section className="section home-daily" aria-labelledby="daily-title">
        <div className="section__header">
          <h2 id="daily-title">{t('home.quests.title')}</h2>
          {quests.data && (
            <span className={`timer-chip${dailyClaimable > 0 ? ' timer-chip--ready' : ''}`}>
              {dailyClaimable > 0 ? t('home.quests.claimable', { count: dailyClaimable }) : t('home.quests.resets', { time: timeUntil(quests.data.daily.resetsAt) })}
            </span>
          )}
        </div>
        {quests.isError ? (
          <ErrorState error={quests.error} onRetry={() => void quests.refetch()} />
        ) : quests.data ? (
          <ul className="quest-list">{quests.data.daily.quests.map((quest) => <QuestRow key={quest.id} quest={quest} />)}</ul>
        ) : <Spinner />}
      </section>

      {quests.data && (
        <details className="disclosure home-weekly" open={weeklyClaimable > 0 ? true : undefined}>
          <summary>
            <h2>{t('home.quests.weeklyTitle')}</h2>
            <span className={`timer-chip${weeklyClaimable > 0 ? ' timer-chip--ready' : ''}`}>
              {weeklyClaimable > 0 ? t('home.quests.claimable', { count: weeklyClaimable }) : t('home.quests.ends', { time: timeUntil(quests.data.weekly.resetsAt) })}
            </span>
            <ChevronIcon size={20} />
          </summary>
          <div className="disclosure__body">
            <ul className="quest-list">{quests.data.weekly.quests.map((quest) => <QuestRow key={quest.id} quest={quest} />)}</ul>
          </div>
        </details>
      )}

      {quests.data && (
        <details className="disclosure home-monthly" open={monthlyClaimable > 0 ? true : undefined}>
          <summary>
            <h2>{t('home.quests.monthlyTitle')}</h2>
            <span className={`timer-chip${monthlyClaimable > 0 ? ' timer-chip--ready' : ''}`}>
              {monthlyClaimable > 0 ? t('home.quests.claimable', { count: monthlyClaimable }) : t('home.quests.ends', { time: timeUntil(quests.data.monthly.resetsAt) })}
            </span>
            <ChevronIcon size={20} />
          </summary>
          <div className="disclosure__body">
            <ul className="quest-list">{quests.data.monthly.quests.map((quest) => <QuestRow key={quest.id} quest={quest} />)}</ul>
          </div>
        </details>
      )}

      <section className="section home-collection" aria-labelledby="summary-title">
        <div className="section__header">
          <h2 id="summary-title"><CollectionIcon size={22} />{t('home.collection.title')}</h2>
          <Link to="/collection" className="text-link">{t('home.collection.open')}</Link>
        </div>
        {me.isError ? <ErrorState error={me.error} onRetry={() => void me.refetch()} /> : stats ? (
          <>
            <p className="section__lead">
              {t('home.collection.characters', { count: stats.uniqueCharacters, formatted: formatNumber(stats.uniqueCharacters) })}
              {', '}
              {t('home.collection.animes', { count: stats.animesCompleted, formatted: formatNumber(stats.animesCompleted) })}
            </p>
            <RarityStrip stats={stats} />
            <Link to="/fragments" className="text-link">{t('home.collection.fragments', { count: stats.fragmentsHeld, formatted: formatNumber(stats.fragmentsHeld) })}</Link>
          </>
        ) : <Spinner />}
      </section>

      <Link to="/moisson" className="home-harvest">
        <img src={english ? bannerEn : bannerFr} alt={t('home.bannerAlt')} width={english ? 1539 : 1024} height={english ? 672 : 447} loading="lazy" />
        <span>{t('home.harvest')}<ChevronIcon size={18} /></span>
      </Link>

      <details className="disclosure home-world">
        <summary><span>{t('home.stats.title')}</span><ChevronIcon size={20} /></summary>
        <div className="disclosure__body">
          {publicStats.isError ? (
            <ErrorState error={publicStats.error} onRetry={() => void publicStats.refetch()} />
          ) : publicStats.data ? (
            <dl className="home-world-stats" aria-label={t('home.stats.title')}>
              {([
                ['onlinePlayers', publicStats.data.onlinePlayers],
                ['registeredPlayers', publicStats.data.registeredPlayers],
                ['cardsInCirculation', publicStats.data.cardsInCirculation],
                ['animesInCirculation', publicStats.data.animesInCirculation],
                ['mythicCardsInCirculation', publicStats.data.mythicCardsInCirculation],
                ['divineCardsInCirculation', publicStats.data.divineCardsInCirculation],
              ] as const).map(([key, value]) => (
                <div className="home-world-stats__item" key={key}><dt>{t(`home.stats.${key}`)}</dt><dd>{formatNumber(value)}</dd></div>
              ))}
            </dl>
          ) : <Spinner />}
        </div>
      </details>
    </div>
  );
}
